import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  CloudArrowUpIcon,
  DocumentArrowDownIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  DocumentTextIcon,
  BuildingOffice2Icon,
  LockClosedIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const BulkUpload: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [uploadedOrders, setUploadedOrders] = useState<any[]>([]);
  const [validationErrors, setValidationErrors] = useState<any[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  const isCorporateApproved = user?.customer_type === 'corporate' && user?.approval_status === 'approved';
  const isCorporatePending = user?.customer_type === 'corporate' && user?.approval_status === 'pending';
  const isCorporateRejected = user?.customer_type === 'corporate' && user?.approval_status === 'rejected';

  if (!isCorporateApproved) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
              <BuildingOffice2Icon className="h-8 w-8 text-orange-600" />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Corporate Feature: Bulk Orders
            </h1>
            
            {isCorporatePending ? (
              <>
                <p className="text-gray-600 mb-6">
                  Your corporate account is currently under review. Once approved, you'll have access to bulk order creation and other exclusive corporate features.
                </p>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-yellow-800">
                    <strong>Status:</strong> Pending Approval - Our team is reviewing your application. You'll receive an email once your account is approved.
                  </p>
                </div>
              </>
            ) : isCorporateRejected ? (
              <>
                <p className="text-gray-600 mb-6">
                  Your corporate account application needs updates. Please review the feedback and resubmit your application.
                </p>
                <button
                  onClick={() => navigate('/pending-approval')}
                  className="inline-flex items-center px-6 py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition-colors"
                >
                  Update Application
                  <ArrowRightIcon className="h-5 w-5 ml-2" />
                </button>
              </>
            ) : (
              <>
                <p className="text-gray-600 mb-6">
                  Bulk order creation is an exclusive feature for corporate customers. Upgrade to a corporate account to access volume pricing, bulk orders, and priority support.
                </p>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6 text-left">
                  <h3 className="font-semibold text-gray-900 mb-3">Corporate Benefits Include:</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                      Bulk order creation via CSV upload
                    </li>
                    <li className="flex items-center">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                      Volume-based pricing discounts
                    </li>
                    <li className="flex items-center">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                      Priority customer support
                    </li>
                    <li className="flex items-center">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                      Dedicated account management
                    </li>
                  </ul>
                </div>
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={() => navigate('/register/customer')}
                    className="inline-flex items-center px-6 py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition-colors"
                  >
                    <BuildingOffice2Icon className="h-5 w-5 mr-2" />
                    Register as Corporate
                  </button>
                  <button
                    onClick={() => navigate('/create-order')}
                    className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                  >
                    Create Single Order
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      // Parse and validate the CSV file
      parseCSV(e.target.files[0]);
    }
  };

  const parseCSV = async (file: File) => {
    // TODO: Implement CSV parsing and validation
    console.log('Parsing CSV file:', file.name);
    
    // Mock data for demonstration
    const mockOrders = [
      {
        id: 1,
        pickup_location: '123 Main St, New York, NY',
        dropoff_location: '456 Park Ave, New York, NY',
        package_description: 'Documents',
        package_weight: 1,
        vehicle_type: 'Bicycle',
        status: 'valid'
      },
      {
        id: 2,
        pickup_location: '789 Broadway, New York, NY',
        dropoff_location: '321 5th Ave, New York, NY',
        package_description: 'Electronics',
        package_weight: 5,
        vehicle_type: 'Sedan',
        status: 'valid'
      }
    ];
    
    setUploadedOrders(mockOrders);
    setShowPreview(true);
  };

  const downloadTemplate = () => {
    const csvContent = `pickup_location,dropoff_location,package_description,package_weight,vehicle_type,special_instructions
"123 Main St, New York, NY","456 Park Ave, New York, NY","Documents",1,"Bicycle",""
"789 Broadway, New York, NY","321 5th Ave, New York, NY","Electronics",5,"Sedan","Handle with care"`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'volexx_bulk_order_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleBulkUpload = async () => {
    // TODO: Implement bulk order creation API call
    console.log('Creating bulk orders:', uploadedOrders);
    alert(`${uploadedOrders.length} orders created successfully!`);
    setFile(null);
    setUploadedOrders([]);
    setShowPreview(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Bulk Upload Orders</h1>
          <p className="mt-2 text-gray-600">Upload multiple orders at once using CSV file</p>
        </div>

        {/* Download Template Card */}
        <div className="bg-white rounded-volexx-lg shadow-volexx-md p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center mb-3">
                <DocumentTextIcon className="h-6 w-6 text-volexx-blue-500 mr-2" />
                <h2 className="text-xl font-bold text-gray-900">Step 1: Download Template</h2>
              </div>
              <p className="text-gray-600 mb-4">
                Download our CSV template to ensure your data is formatted correctly
              </p>
              <button
                onClick={downloadTemplate}
                className="btn-secondary flex items-center"
              >
                <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                Download CSV Template
              </button>
            </div>
          </div>

          <div className="mt-6 p-4 bg-volexx-blue-50 rounded-volexx border border-volexx-blue-200">
            <h3 className="text-sm font-semibold text-volexx-blue-900 mb-2">Required Fields</h3>
            <ul className="text-sm text-volexx-blue-700 space-y-1">
              <li>• <strong>pickup_location</strong> - Full pickup address</li>
              <li>• <strong>dropoff_location</strong> - Full delivery address</li>
              <li>• <strong>package_description</strong> - Description of the package</li>
              <li>• <strong>package_weight</strong> - Weight in kg (numeric)</li>
              <li>• <strong>vehicle_type</strong> - Bicycle, Motorbike, Sedan, SUV, Van, etc.</li>
              <li>• <strong>special_instructions</strong> - Optional delivery notes</li>
            </ul>
          </div>
        </div>

        {/* Upload File Card */}
        <div className="bg-white rounded-volexx-lg shadow-volexx-md p-6 mb-6">
          <div className="flex items-center mb-4">
            <CloudArrowUpIcon className="h-6 w-6 text-volexx-orange-500 mr-2" />
            <h2 className="text-xl font-bold text-gray-900">Step 2: Upload CSV File</h2>
          </div>

          <div className="border-2 border-dashed border-gray-300 rounded-volexx p-8 text-center">
            {!file ? (
              <div>
                <CloudArrowUpIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <label className="cursor-pointer">
                  <span className="btn-primary inline-block">
                    Choose File
                  </span>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </label>
                <p className="text-sm text-gray-500 mt-3">
                  or drag and drop your CSV file here
                </p>
              </div>
            ) : (
              <div>
                <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <p className="text-lg font-semibold text-gray-900 mb-2">{file.name}</p>
                <p className="text-sm text-gray-500 mb-4">
                  {(file.size / 1024).toFixed(2)} KB
                </p>
                <button
                  onClick={() => {
                    setFile(null);
                    setUploadedOrders([]);
                    setShowPreview(false);
                  }}
                  className="text-sm text-red-600 hover:text-red-700 font-semibold"
                >
                  Remove File
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Preview and Validation */}
        {showPreview && (
          <div className="bg-white rounded-volexx-lg shadow-volexx-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Step 3: Review and Confirm ({uploadedOrders.length} orders)
            </h2>

            {validationErrors.length > 0 && (
              <div className="mb-4 p-4 bg-red-50 rounded-volexx border border-red-200">
                <div className="flex items-center mb-2">
                  <ExclamationCircleIcon className="h-5 w-5 text-red-600 mr-2" />
                  <h3 className="font-semibold text-red-900">Validation Errors</h3>
                </div>
                <ul className="text-sm text-red-700 space-y-1">
                  {validationErrors.map((error, index) => (
                    <li key={index}>Row {error.row}: {error.message}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="overflow-x-auto mb-6">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      #
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Pickup
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Dropoff
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Package
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Weight
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Vehicle
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {uploadedOrders.map((order, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 text-sm text-gray-900">{index + 1}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                        {order.pickup_location}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                        {order.dropoff_location}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {order.package_description}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {order.package_weight}kg
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {order.vehicle_type}
                      </td>
                      <td className="px-4 py-3">
                        {order.status === 'valid' ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-500" />
                        ) : (
                          <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-volexx">
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  Total Orders: {uploadedOrders.length}
                </p>
                <p className="text-sm text-gray-600">
                  All orders validated and ready to upload
                </p>
              </div>
              <button
                onClick={handleBulkUpload}
                disabled={validationErrors.length > 0}
                className="btn-primary disabled:opacity-50"
              >
                Confirm & Create Orders
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkUpload;
